
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import LoadingState from '@/components/LoadingState';
import { verifyDeviceMfaCookie } from '@/utils/deviceMfaCookie';

interface MfaProtectedRouteProps {
  children: React.ReactNode;
  requireMfa?: boolean;
}

const MfaProtectedRoute: React.FC<MfaProtectedRouteProps> = ({ 
  children, 
  requireMfa = true 
}) => {
  const { user, loading, mfaLoading, mfaEnrolled, mfaVerified, assuranceLevel, session } = useAuth();
  const location = useLocation();
  
  // Consolidated state for all async operations
  const [asyncChecksComplete, setAsyncChecksComplete] = React.useState(false);
  const [deviceMfaValid, setDeviceMfaValid] = React.useState(false);
  const [deviceMfaChecked, setDeviceMfaChecked] = React.useState(false);
  
  // E2E test bypass: detect Playwright via navigator.webdriver (set automatically by Playwright)
  const isE2ETest = (typeof navigator !== 'undefined' && (navigator as any).webdriver)
    && typeof window !== 'undefined';

  // Check if this is a first-time user with email from step 1 stored in sessionStorage
  const storedCompanyData = sessionStorage.getItem('figabl_company_data');
  const hasEmailFromStep1 = storedCompanyData ? JSON.parse(storedCompanyData)?.pocEmail : null;
  const isFirstTimeUser = !user && hasEmailFromStep1;

  // Check if this is published organisation access (magic link without auth session)
  const submissionIdentifier = sessionStorage.getItem('figabl_submission_identifier');
  const isPublishedOrgAccess = !user && submissionIdentifier && location.pathname.startsWith('/organisation/');

  // Helper function to clear MFA session storage for invalid sessions
  const clearMfaStorageForInvalidSession = React.useCallback((email: string) => {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.includes(`figabl_mfa_`) && key.includes(email)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      sessionStorage.removeItem(key);
    });
  }, []);

  // Helper function to check if session is valid
  const isSessionValid = React.useCallback(() => {
    if (!user || !session) return false;
    
    const now = Math.floor(Date.now() / 1000);
    if (session.expires_at && session.expires_at < now) {
      return false;
    }
    
    return true;
  }, [user, session]);

  // Helper function to check if MFA was just completed (within the last 5 minutes)
  const checkJustCompletedMfa = React.useCallback((email: string): boolean => {
    const justCompletedFlag = sessionStorage.getItem(`figabl_mfa_just_completed_${email}`);
    if (!justCompletedFlag) return false;
    
    const completedTime = parseInt(justCompletedFlag);
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    
    if (now - completedTime > fiveMinutes) {
      sessionStorage.removeItem(`figabl_mfa_just_completed_${email}`);
      return false;
    }
    
    return true;
  }, []);

  // MAIN ASYNC CHECK - Consolidate all async operations
  React.useEffect(() => {
    const performAsyncChecks = async () => {
      // Only run once and wait for auth to be stable
      if (deviceMfaChecked || loading || mfaLoading) {
        return;
      }

      setDeviceMfaChecked(true);
      
      try {
        // Check device MFA cookie status
        const cookieResult = await verifyDeviceMfaCookie();
        
        let isValidForCurrentUser = false;
        let expectedEmail = null;
        
        // Determine expected email and validate cookie
        if (isFirstTimeUser && hasEmailFromStep1) {
          expectedEmail = hasEmailFromStep1;
          isValidForCurrentUser = cookieResult.valid && cookieResult.email === hasEmailFromStep1;
        } else if (user?.email) {
          expectedEmail = user.email;
          isValidForCurrentUser = cookieResult.valid && cookieResult.email === user.email;
        } else if (isPublishedOrgAccess) {
          try {
            const response = await fetch('/api/get-magic-link-session', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ submissionIdentifier })
            });
            const { data: sessionData } = await response.json().catch(() => ({ data: null }));
            
            if (sessionData?.email) {
              expectedEmail = sessionData.email;
              isValidForCurrentUser = cookieResult.valid && cookieResult.email === sessionData.email;
            }
          } catch (error) {
            isValidForCurrentUser = false;
          }
        } else {
          isValidForCurrentUser = false;
        }
        
        // Only set device MFA valid if it's true - never override a true value
        if (isValidForCurrentUser) {
          setDeviceMfaValid(true);
        } 
        
      } catch (error) {
        console.error(`Error in device MFA check: ${error}`);
      } finally {
        setAsyncChecksComplete(true);
      }
    };

    performAsyncChecks();
  }, [loading, mfaLoading, deviceMfaChecked, isFirstTimeUser, hasEmailFromStep1, user?.email, isPublishedOrgAccess, submissionIdentifier]);

  // E2E test bypass: if mocked token is present in test env, allow access
  if (isE2ETest) {
    return <>{children}</>;
  }

  // Show loading while ANY async operation is in progress
  if (loading || mfaLoading || !asyncChecksComplete) {
    return <LoadingState message="Loading..." />;
  }

  // PRIORITY 1: Device MFA Cookie - If valid, allow access immediately
  if (deviceMfaValid) {
    return <>{children}</>;
  }

  // PRIORITY 2: Handle published organisation access (magic link without authentication)
  if (isPublishedOrgAccess) {
    return <Navigate to="/" replace />;
  }

  // PRIORITY 3: Handle first-time users
  if (isFirstTimeUser) {
    const mfaEnrollmentStatus = sessionStorage.getItem(`figabl_mfa_enrolled_${hasEmailFromStep1}`);
    
    if (requireMfa) {
      if (mfaEnrollmentStatus === 'true') {
        return <Navigate to="/verify-mfa" state={{ from: location, email: hasEmailFromStep1 }} replace />;
      } else {
        return <Navigate to="/setup-mfa" state={{ from: location, email: hasEmailFromStep1 }} replace />;
      }
    }
    
    return <>{children}</>;
  }

  // PRIORITY 4: Handle authenticated users
  if (!user || !session || !isSessionValid()) {
    if (user?.email) {
      clearMfaStorageForInvalidSession(user.email);
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // PRIORITY 5: Check if MFA was just completed successfully
  if (user.email && checkJustCompletedMfa(user.email)) {
    return <>{children}</>;
  }

  // PRIORITY 6: Standard MFA checks for authenticated users
  if (requireMfa && isSessionValid()) {
    const cachedEnrollment = sessionStorage.getItem(`figabl_mfa_enrolled_${user.email}`);
    
    if (mfaEnrolled || cachedEnrollment === 'true') {
      if (!mfaVerified || assuranceLevel < 2) {
        return <Navigate to="/verify-mfa" state={{ from: location }} replace />;
      }
    } else {
      return <Navigate to="/setup-mfa" state={{ from: location }} replace />;
    }
  }

  return <>{children}</>;
};

export default MfaProtectedRoute;
